// ── Central business configuration — the single source of truth ──────────
// Contact details are the client's real business information (supplied 2026-08-24).
// Items marked "pending owner confirmation" must be re-verified by Salim Kazi
// before production launch — see /app/memory/PRODUCTION_REPORT.md.
export const SITE = {
  name: "Duct Clean Co.",
  short: "DCC",
  // legalName intentionally omitted — registered entity name unpublished until owner confirms
  tagline: "Specialist duct cleaning. Planned around your facility. Proven at handover.",
  description: "Specialist commercial duct, exhaust and hygiene-systems cleaning for operating facilities across India. Kitchen exhaust, robotic air duct, laundry duct, drain & grease trap, dry ice blasting — surveyed, executed and verified with documented handover.",
  brandStatement: "FASTER • SAFER • CLEANER",
  philosophy: "INSPECT → ENGINEER → EXECUTE → PROVE",
  methodology: ["Survey", "Plan", "Execute", "Verify", "Handover"],
  evidenceLoop: "INSPECT → CLEAN → VERIFY → DOCUMENT",
  // Canonical hostname is the NON-www apex (www permanently redirects to it).
  url: "https://ductcleanco.com",
  contact: {
    phone: "+91 84339 91469",
    whatsapp: "+91 84339 91469", // pending owner confirmation: WhatsApp-enabled number
    email: "growth@ductcleanco.com",
    hq: "Seawoods, Navi Mumbai, India",
    // owner-supplied registered office address (2026-08-30, final wording)
    address: "Office No. F-407, F-Wing, 4th Floor, Tower II, L&T Seawoods Grand Central, Plot No. R-1, Sector 40, Seawoods Railway Station, Nerul, Navi Mumbai – 400706, Maharashtra, India.",
    addressShort: "Office No. F-407, F-Wing, 4th Floor, Tower II, L&T Seawoods Grand Central, Plot No. R-1, Sector 40, Seawoods Railway Station, Nerul, Navi Mumbai – 400706, Maharashtra, India.",
    // Direct link to the Duct Clean Co. Google Maps place profile (owner-supplied
    // share link, resolves to the place entity — never a bare address search).
    mapsUrl: "https://www.google.com/maps/place/Duct+Clean+Co/@19.0204678,73.0175447,17z/data=!3m1!4b1!4m6!3m5!1s0x3be7ce4a029a7561:0x8aa7987e75791d55!8m2!3d19.0204627!4d73.0201196!16s%2Fg%2F11c3tgnqbt",
    hours: "Mon–Sat, 09:00–19:00 IST",
  },
  social: {
    linkedin: "", // pending owner: official LinkedIn URL (footer link hidden until provided)
    instagram: "", // pending owner: official Instagram URL
  },
  nav: [
    { label: "Services", to: "/services" },
    { label: "Industries", to: "/industries" },
    { label: "Our Work", to: "/our-work" },
    { label: "Clients", to: "/clients" },
    { label: "Locations", to: "/locations" },
    { label: "Insights", to: "/insights" },
    { label: "About", to: "/about" },
  ],
  cta: { label: "Request a Free Site Survey", to: "/request-survey" },
};

// ── Verified track record — every number shown anywhere on the site reads
// from this object. Each metric carries its definition, measurement period,
// internal source and verification status. Nothing is published without an
// owner-confirmed definition — see /app/memory/VERIFICATION_CHECKLIST.md.
export const FACTS = {
  marriottProperties: { value: 80, suffix: "+", label: "Marriott properties", caption: "CLIENT EXPERIENCE", unit: "properties (facilities)", asOf: "2026-08", sourceNote: "DCC client records — Marriott portfolio engagements", verification: "PENDING — owner to re-confirm count and measurement period" },
  clientsServed: { value: 1000, suffix: "+", label: "Clients served", caption: "ACROSS INDIA", unit: "client organisations", asOf: "2026-08", sourceNote: "DCC client records", verification: "OWNER-CONFIRMED 2026-08-30" },
  sitesPerMonth: { value: 200, suffix: "+", label: "Sites executed", caption: "EACH MONTH", unit: "sites per month (average)", asOf: "2026-08", sourceNote: "DCC operations tracker", verification: "PENDING — owner to confirm monthly average and measurement window" },
  yearsExperience: { value: 10, suffix: "+", label: "Years", caption: "INDUSTRY EXPERIENCE", unit: "years", asOf: "2026-08", sourceNote: "Company record", verification: "OWNER-CONFIRMED 2026-08-30" },
};

// ── Standards & industry frameworks — labels describe the ACTUAL relationship.
// Memberships confirmed by the owner (2026-08-28); re-verify active status
// before launch. NFPA 96 and FSSAI Schedule 4 are standards/frameworks DCC
// works to — never presented as memberships or certifications.
export const STANDARDS = [
  {
    id: "nadca", short: "NADCA", name: "National Air Duct Cleaners Association",
    cardName: "National Air Duct Cleaners Association",
    src: "/assets/certs/nadca.png", relation: "Member",
    note: "The global association for HVAC inspection, cleaning and restoration. DCC's membership aligns its duct-cleaning method with the NADCA ACR standard.",
  },
  {
    id: "ikeca", short: "IKECA", name: "International Kitchen Exhaust Cleaning Association",
    cardName: "International Kitchen Exhaust Cleaning Association",
    src: "/assets/certs/ikeca.png", relation: "Member",
    note: "The international association for kitchen exhaust cleaning specialists. Membership keeps DCC's exhaust-cleaning method aligned with IKECA's codes and best practices.",
  },
  {
    id: "nfpa", short: "NFPA 96", name: "National Fire Protection Association",
    cardName: "Commercial Cooking Operations — Ventilation and Fire Protection Standard",
    src: "/assets/certs/nfpa.png", relation: "Referenced standard",
    note: "NFPA 96 is the referenced fire-safety standard for ventilation control and fire protection of commercial cooking operations. DCC scopes and verifies kitchen exhaust work against it. This is a standard DCC works to — not a membership.",
  },
  {
    id: "fssai", short: "FSSAI — Schedule 4", name: "Food Safety and Standards Authority of India",
    cardName: "Food Safety and Hygiene Requirements for Food Businesses",
    src: "/assets/certs/fssai.png", relation: "Applicable hygiene framework",
    note: "FSSAI Schedule 4 sets the sanitary and hygiene requirements Indian food businesses must meet. DCC's kitchen hygiene and exhaust work helps client facilities satisfy them. This is an applicable framework — not a certification held by DCC.",
  },
  {
    id: "fsai", short: "FSAI", name: "Fire & Security Association of India",
    cardName: "Fire & Security Association of India",
    src: "/assets/certs/fsai.png", relation: "Member",
    note: "India's association for fire and security professionals. DCC's membership connects its fire-risk-sensitive exhaust work to the national fire-safety community.",
  },
];

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
